import React, { useState, useEffect, useCallback } from "react";
import { baseUrl } from "../../../../constants/api";
import { useDispatch, useSelector } from "react-redux";
import showToastMessage from "../../../../utils/showToastMessage";
import { setTopics, RootState } from "../../../../store";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API_ENDPOINTS } from "../../../../constants/endpoints";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import CertificateTemplateDesigner from "./CertificateTemplateDesigner";

type CertificateTemplate = {
  _id: string;
  name: string;
  orientation: "landscape" | "portrait";
  background_url: string;
  fields: Array<{
    key: string;
    x: number;
    y: number;
    fontSize: number;
    align: "left" | "center" | "right";
    color?: string;
  }>;
};

const CreateQuiz = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { token } = useSelector((state: RootState) => state.account);
  const { topics } = useSelector((state: RootState) => state.quiz) as { topics: any[] };
  const [selectedTopic, setSelectedTopic] = useState("");
  const [selectedSubtopic, setSelectedSubtopic] = useState("");
  const [quizName, setQuizName] = useState("");
  const [noOfQuizzes, setNoOfQuizzes] = useState(1);
  const [noOfQuestions, setNoOfQuestions] = useState(20);
  const [quizDuration, setQuizDuration] = useState(30);
  const [quizType, setQuizType] = useState<"regular" | "certification">("regular");
  const [passingPercentage, setPassingPercentage] = useState(50);
  const [availableUntil, setAvailableUntil] = useState("");
  const [certificateTemplates, setCertificateTemplates] = useState<CertificateTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templateDesignerOpen, setTemplateDesignerOpen] = useState(false);
  const [confirmationVisible, setConfirmationVisible] = useState(false);

  const fetchTopics = useCallback(async () => {
    const apiUrl = `${baseUrl}/api/topic/get-all-topics`;
    setLoading(true);
    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const topicData = await response.json();
        dispatch(setTopics(topicData.data));
      } else {
        message.error("Failed to fetch topics.");
      }
    } catch (error) {
      message.error("An error occurred while fetching topics.");
    } finally {
      setLoading(false);
    }
  }, [token, dispatch]);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  const fetchCertificateTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${baseUrl}${API_ENDPOINTS.GET_CERTIFICATE_TEMPLATES}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setCertificateTemplates(data.templates || []);
      }
    } catch (error) {
      message.error("An error occurred while fetching certificate templates.");
    }
  }, [token]);

  useEffect(() => {
    fetchCertificateTemplates();
  }, [fetchCertificateTemplates]);

  const validateForm = () => {
    const now = Date.now();
    const selectedDueDateMs = availableUntil ? new Date(availableUntil).getTime() : NaN;

    if (!selectedTopic) { message.error("Please select a topic"); return false; }
    if (!selectedSubtopic) { message.error("Please select a subtopic"); return false; }
    if (!noOfQuizzes || noOfQuizzes <= 0) { message.error("Please enter a valid number of quizzes"); return false; }
    if (!quizDuration || quizDuration <= 0) { message.error("Please enter a valid quiz duration"); return false; }
    if (quizType !== "certification" && !availableUntil) { message.error("Please select the quiz due date"); return false; }
    if (quizType !== "certification" && Number.isNaN(selectedDueDateMs)) { message.error("Please select a valid quiz due date"); return false; }
    if (quizType !== "certification" && selectedDueDateMs <= now) {
      message.error("Quiz due date must be later than the current time");
      return false;
    }
    if (quizType === "certification" && (!passingPercentage || passingPercentage <= 0 || passingPercentage > 100)) {
      message.error("Please enter a valid passing percentage");
      return false;
    }
    if (quizType === "certification" && !selectedTemplateId) {
      message.error("Please select a certificate template");
      return false;
    }
    return true;
  };

  const handleCreateQuizConfirmation = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) setConfirmationVisible(true);
  };

  const handleCreateQuiz = async () => {
    const apiUrl = `${baseUrl}/api/quiz/create-quiz`;
    const quizData = {
      number_of_quizzes: noOfQuizzes,
      topic_name: selectedTopic,
      sub_topic_name: selectedSubtopic,
      number_of_questions: noOfQuestions,
      quiz_duration: quizDuration,
      quiz_name: quizName,
      quiz_type: quizType,
      passing_percentage: passingPercentage,
      certificate_template_id: quizType === "certification" ? selectedTemplateId : null,
      available_until: quizType === "certification" ? null : (availableUntil ? new Date(availableUntil).toISOString() : null),
    };
    setLoading(true);
    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(quizData),
      });
      if (response.ok) {
        await response.json();
        message.success("Quiz created successfully!");
        setConfirmationVisible(false);
        setSelectedTopic("");
        setSelectedSubtopic("");
        setQuizName("");
        setNoOfQuizzes(1);
        setNoOfQuestions(20);
        setQuizDuration(30);
        setQuizType("regular");
        setPassingPercentage(50);
        setAvailableUntil("");
        setSelectedTemplateId("");
        navigate("/");
      } else {
        const errorText = await response.text();
        showToastMessage(`Failed to create quiz: ${errorText}`, "error");
      }
    } catch (error) {
      showToastMessage("An error occurred while creating quiz.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleTopicChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTopic(e.target.value);
    setSelectedSubtopic("");
  };

  const selectedTopicObj = topics.find((t: any) => t.topic_name === selectedTopic);
  const selectedTemplate = certificateTemplates.find((template) => template._id === selectedTemplateId);
  const totalQuestions = noOfQuizzes * noOfQuestions;

  return (
    <div className="cq-container">
      <form onSubmit={handleCreateQuizConfirmation}>
        <div className="cq-layout">

          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <div className="cq-card">
              <div className="cq-card-header">
                <div className="cq-card-header-icon">🗂️</div>
                <div>
                  <h3>Topic Selection</h3>
                  <p>Choose the topic and subtopic for this quiz</p>
                </div>
              </div>
              <div className="cq-card-body">
                <div className="cq-field">
                  <label className="cq-label" htmlFor="topic">
                    Topic <span>*</span>
                  </label>
                  <select
                    id="topic"
                    className="cq-select"
                    value={selectedTopic}
                    onChange={handleTopicChange}
                    required
                  >
                    <option value="">— Select a topic —</option>
                    {topics.map((topic: any) => (
                      <option key={topic._id} value={topic.topic_name}>
                        {topic.topic_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="cq-field">
                  <label className="cq-label" htmlFor="subtopic">
                    Subtopic <span>*</span>
                  </label>
                  <select
                    id="subtopic"
                    className="cq-select"
                    value={selectedSubtopic}
                    onChange={(e) => setSelectedSubtopic(e.target.value)}
                    required
                    disabled={!selectedTopic}
                  >
                    <option value="">
                      {selectedTopic ? "— Select a subtopic —" : "Select a topic first"}
                    </option>
                    {selectedTopicObj?.subtopics.map((sub: string) => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </select>
                </div>

                <div className="cq-field" style={{ marginTop: '10px' }}>
                  <label className="cq-label" htmlFor="quizName">
                    Quiz Name (Optional)
                  </label>
                  <Input
                    id="quizName"
                    placeholder={`${selectedTopic || "Topic"} - ${selectedSubtopic || "Subtopic"} - ${new Date().toLocaleDateString('en-GB')}`}
                    value={quizName}
                    onChange={(e) => setQuizName(e.target.value)}
                    className="h-12 rounded-xl border-[1.5px] border-[#e0e0e0] bg-[#f8f9ff]"
                  />
                  <p style={{ fontSize: '11px', color: '#888', margin: '2px 0 0 0' }}>
                    Leave empty to use default name
                  </p>
                </div>
              </div>
            </div>

            <div className="cq-card">
              <div className="cq-card-header">
                <div className="cq-card-header-icon">⚙️</div>
                <div>
                  <h3>Quiz Settings</h3>
                  <p>Configure the quiz parameters</p>
                </div>
              </div>
              <div className="cq-card-body">
                <div className="cq-field">
                  <label className="cq-label">Quiz Type <span>*</span></label>
                  <select
                    className="cq-select"
                    value={quizType}
                    onChange={(e) => setQuizType(e.target.value as "regular" | "certification")}
                  >
                    <option value="regular">Regular Quiz</option>
                    <option value="certification">Certification Quiz</option>
                  </select>
                </div>

                {quizType === "certification" && (
                  <>
                    <div className="cq-field">
                      <label className="cq-label">Passing Percentage <span>*</span></label>
                      <Input
                        type="number"
                        min={1}
                        max={100}
                        value={passingPercentage}
                        onChange={(e) => setPassingPercentage(Math.min(100, Math.max(1, Number(e.target.value) || 50)))}
                        className="h-12 rounded-xl border-[1.5px] border-[#e0e0e0] bg-[#f8f9ff]"
                      />
                    </div>
                    <div className="cq-field">
                      <label className="cq-label">Certificate Template <span>*</span></label>
                      <div className="flex gap-3">
                        <select
                          className="cq-select"
                          value={selectedTemplateId}
                          onChange={(e) => setSelectedTemplateId(e.target.value)}
                        >
                          <option value="">— Select a certificate template —</option>
                          {certificateTemplates.map((template) => (
                            <option key={template._id} value={template._id}>
                              {template.name} ({template.orientation === "portrait" ? "A4 Portrait" : "A4 Landscape"})
                            </option>
                          ))}
                        </select>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setTemplateDesignerOpen(true)}
                          className="h-12 rounded-xl"
                        >
                          New Template
                        </Button>
                      </div>
                      {selectedTemplate && (
                        <div className="mt-3 rounded-2xl border border-[#ececec] bg-[#fafafa] p-3">
                          <div className="text-sm font-semibold text-[#222]">{selectedTemplate.name}</div>
                          <div className="text-xs text-[#666]">
                            {selectedTemplate.orientation === "portrait" ? "A4 Portrait" : "A4 Landscape"} • {selectedTemplate.fields?.length || 0} field(s)
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {quizType !== "certification" && (
                  <div className="cq-field">
                    <label className="cq-label">Due Date <span>*</span></label>
                    <Input
                      type="datetime-local"
                      value={availableUntil}
                      onChange={(e) => setAvailableUntil(e.target.value)}
                      className="h-12 rounded-xl border-[1.5px] border-[#e0e0e0] bg-[#f8f9ff]"
                    />
                    <p style={{ fontSize: '11px', color: '#888', margin: '2px 0 0 0' }}>
                      Quiz will be unavailable after this date and time.
                    </p>
                  </div>
                )}

                <div className="cq-field">
                  <label className="cq-label">Number of Quizzes <span>*</span></label>
                  <div className="cq-number-wrap">
                    <button
                      type="button"
                      className="cq-stepper"
                      onClick={() => setNoOfQuizzes((v) => Math.max(1, Number(v) - 1))}
                    >−</button>
                    <div className="cq-divider" />
                    <input
                      type="number"
                      className="cq-number-input"
                      value={noOfQuizzes}
                      onChange={(e) => setNoOfQuizzes(Math.max(1, Number(e.target.value)))}
                      min="1"
                      required
                    />
                    <div className="cq-divider" />
                    <button
                      type="button"
                      className="cq-stepper"
                      onClick={() => setNoOfQuizzes((v) => Number(v) + 1)}
                    >+</button>
                  </div>
                </div>

                <div className="cq-field">
                  <label className="cq-label">Questions per Quiz <span>*</span></label>
                  <div className="cq-number-wrap">
                    <button
                      type="button"
                      className="cq-stepper"
                      onClick={() => setNoOfQuestions((v) => Math.max(1, Number(v) - 1))}
                    >−</button>
                    <div className="cq-divider" />
                    <input
                      type="number"
                      className="cq-number-input"
                      value={noOfQuestions}
                      onChange={(e) => setNoOfQuestions(Math.max(1, Number(e.target.value)))}
                      min="1"
                      required
                    />
                    <div className="cq-divider" />
                    <button
                      type="button"
                      className="cq-stepper"
                      onClick={() => setNoOfQuestions((v) => Number(v) + 1)}
                    >+</button>
                  </div>
                </div>

                <div className="cq-field">
                  <label className="cq-label">Duration (minutes) <span>*</span></label>
                  <div className="cq-number-wrap">
                    <button
                      type="button"
                      className="cq-stepper"
                      onClick={() => setQuizDuration((v) => Math.max(1, Number(v) - 5))}
                    >−</button>
                    <div className="cq-divider" />
                    <input
                      type="number"
                      className="cq-number-input"
                      value={quizDuration}
                      onChange={(e) => setQuizDuration(Math.max(1, Number(e.target.value)))}
                      min="1"
                      required
                    />
                    <div className="cq-divider" />
                    <button
                      type="button"
                      className="cq-stepper"
                      onClick={() => setQuizDuration((v) => Number(v) + 5)}
                    >+</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="cq-summary-card">
            <p className="cq-summary-title">📊 Quiz Summary</p>
            <div className="cq-summary-rows">
              <div className="cq-summary-row">
                <span className="cq-summary-key">Topic</span>
                <span className={`cq-summary-val ${!selectedTopic ? "placeholder" : ""}`}>
                  {selectedTopic || "Not selected"}
                </span>
              </div>
              <div className="cq-summary-row">
                <span className="cq-summary-key">Subtopic</span>
                <span className={`cq-summary-val ${!selectedSubtopic ? "placeholder" : ""}`}>
                  {selectedSubtopic || "Not selected"}
                </span>
              </div>
              <div className="cq-summary-row">
                <span className="cq-summary-key">Quiz Name</span>
                <span className={`cq-summary-val ${!quizName ? "placeholder" : ""}`}>
                  {quizName || `${selectedTopic || "Topic"} - ${selectedSubtopic || "Subtopic"} - ${new Date().toLocaleDateString('en-GB')}`}
                </span>
              </div>
              <div className="cq-summary-row">
                <span className="cq-summary-key">Type</span>
                <span className="cq-summary-val" style={{ textTransform: "capitalize" }}>{quizType}</span>
              </div>
              {quizType === "certification" && (
                <>
                  <div className="cq-summary-row">
                    <span className="cq-summary-key">Passing Mark</span>
                    <span className="cq-summary-val">{passingPercentage}%</span>
                  </div>
                  <div className="cq-summary-row">
                    <span className="cq-summary-key">Template</span>
                    <span className={`cq-summary-val ${!selectedTemplate ? "placeholder" : ""}`}>
                      {selectedTemplate ? `${selectedTemplate.name} (${selectedTemplate.orientation === "portrait" ? "A4 Portrait" : "A4 Landscape"})` : "Not selected"}
                    </span>
                  </div>
                </>
              )}
              {quizType !== "certification" && (
                <div className="cq-summary-row">
                  <span className="cq-summary-key">Due Date</span>
                  <span className={`cq-summary-val ${!availableUntil ? "placeholder" : ""}`}>
                    {availableUntil ? new Date(availableUntil).toLocaleString('en-GB') : "Not selected"}
                  </span>
                </div>
              )}
              <div className="cq-summary-row">
                <span className="cq-summary-key">Quizzes</span>
                <span className="cq-summary-val">{noOfQuizzes}</span>
              </div>
              <div className="cq-summary-row">
                <span className="cq-summary-key">Questions each</span>
                <span className="cq-summary-val">{noOfQuestions}</span>
              </div>
              <div className="cq-summary-row">
                <span className="cq-summary-key">Duration</span>
                <span className="cq-summary-val">{quizDuration} min</span>
              </div>
            </div>
            <div className="cq-summary-divider" />
            <div className="cq-summary-stat">
              <span className="cq-summary-stat-num">{totalQuestions}</span>
              <span className="cq-summary-stat-label">Total questions across all quizzes</span>
            </div>
            <Button
              type="submit"
              className="cq-submit-btn w-full"
              disabled={loading}
            >
              {loading ? (
                <><span className="cq-spinner" /> Creating…</>
              ) : (
                <>✨ Create Quiz</>
              )}
            </Button>
          </div>
        </div>
      </form>

      <Dialog open={confirmationVisible} onOpenChange={setConfirmationVisible}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Quiz Creation</DialogTitle>
          </DialogHeader>
          <div className="cq-confirm-content py-2">
            <p className="text-sm text-gray-700 leading-relaxed">
              You're about to create{" "}
              <span className="cq-confirm-highlight">{noOfQuizzes} quiz{noOfQuizzes > 1 ? "zes" : ""}</span>{" "}
              for{" "}
              <span className="cq-confirm-highlight">{selectedTopic} › {selectedSubtopic}</span>
              , each with{" "}
              <span className="cq-confirm-highlight">{noOfQuestions} questions</span>{" "}
              and a{" "}
              <span className="cq-confirm-highlight">{quizDuration}-minute</span>{" "}
              time limit.
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              This will generate {noOfQuizzes * noOfQuestions} total questions. Are you sure?
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmationVisible(false)}>Cancel</Button>
            <Button
              onClick={handleCreateQuiz}
              disabled={loading}
              style={{ background: "linear-gradient(135deg, #4361ee, #3a0ca3)", border: "none", fontWeight: 600 }}
            >
              {loading ? "Creating…" : "Yes, Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={templateDesignerOpen} onOpenChange={setTemplateDesignerOpen}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Certificate Template</DialogTitle>
          </DialogHeader>
          <CertificateTemplateDesigner
            token={token || ""}
            onCancel={() => setTemplateDesignerOpen(false)}
            onCreated={(template) => {
              setCertificateTemplates((prev) => [template, ...prev]);
              setSelectedTemplateId(template._id);
              setTemplateDesignerOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreateQuiz;
